import { forwardRef, memo } from "react";
import { Box, Divider, Fade, IconButton, Stack, SxProps, Typography } from "@mui/material";

import { useSmartSearch } from "@/contexts/useSmartSearch";
import { SmartSearchMasterData } from "@/libs/types/smart-search.type";
import color from "@/styles/color";
import { SearchHistoyListCookieKey } from "@/libs/constant/smart-search.constant";
import IconLoader from "@/components/IconLoader";
import ChipCustom from "@/components/Chip/ChipCustom";
import { useTranslation } from "@/i18n/client";
import { deleteCookieFromServer } from "@/app/api/cookies/cookie";

const textSx = {
  fontSize: { xs: "0.875rem", sm: "1rem" },
  color: color.black200,
} as SxProps;

type SmartSearchResultProps = {
  containerSx?: SxProps;
  searchHistoryList: Nullable<SmartSearchMasterData[]>;
  onSelect: (item: SmartSearchMasterData) => void;
  onClerHistory: () => void;
};

const SmartSearchResult = forwardRef<HTMLDivElement, SmartSearchResultProps>(
  ({ containerSx, searchHistoryList, onSelect, onClerHistory }, ref) => {
    const { t } = useTranslation();
    const { isFocus, searchText, searchResult } = useSmartSearch();

    const highlightText = (text: string) => {
      if (!searchText) return text;
      const parts = text.split(new RegExp(`(${searchText})`, "gi"));
      return parts.map((part, index) =>
        part.toLowerCase() === searchText.toLowerCase() ? (
          <span
            key={index}
            style={{
              background: color.blackO1,
            }}
          >
            <Typography
              component="span"
              sx={{
                background: color.redGradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: "bold",
              }}
            >
              {part}
            </Typography>
          </span>
        ) : (
          part
        ),
      );
    };

    const SearchHistory = () => {
      return (
        <Stack gap={2} paddingBottom={1}>
          <Stack gap={1} flexDirection="row" alignItems="center">
            <Typography
              sx={{
                ...textSx,
                pointerEvents: "none",
                fontWeight: 700,
                background: color.redGradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("SmartSearch.history")}
            </Typography>
            {searchHistoryList && (
              <IconButton
                onClick={() => {
                  deleteCookieFromServer(SearchHistoyListCookieKey);
                  onClerHistory();
                }}
                sx={{
                  height: "auto",
                  width: "30px",
                  aspectRatio: 1,
                  color: color.white,
                  background: color.redGradient,
                }}
              >
                <IconLoader
                  iconName="BinIcon"
                  sx={{
                    fontSize: "14px",
                  }}
                />
              </IconButton>
            )}
          </Stack>
          <Stack gap={1} flexDirection="row" alignItems="center" flexWrap="wrap">
            {searchHistoryList?.map((history) => {
              const {
                brand: { id: brandId },
              } = history;
              return (
                <ChipCustom
                  key={`search-history-${brandId}${history.model ? `-${history.model.id}` : ""}`}
                  label={history.fullTextSearch}
                  containerStyles={{
                    borderColor: "#F10503",
                    cursor: "pointer",
                    minHeight: { xs: "32px", md: "32px", lg: "32px" },
                  }}
                  labelStyles={{
                    background: color.redGradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: 700,
                    fontSize: { xs: "12px", md: "12px", lg: "12px" },
                    lineHeight: { xs: "23px", md: "23px", lg: "23px" },
                  }}
                  onClick={() => onSelect(history)}
                />
              );
            })}
          </Stack>
        </Stack>
      );
    };

    return (
      <>
        <Fade in={isFocus}>
          <Stack
            ref={ref}
            sx={{
              position: "absolute",
              width: "100%",
              height: "auto",
              zIndex: 100,
              paddingTop: 0,
              borderBottomLeftRadius: "24px",
              borderBottomRightRadius: "24px",
              backgroundColor: color.formField,
              ...containerSx,
            }}
          >
            <Divider sx={{ marginX: 3 }} />
            <Stack
              gap={1}
              sx={{
                padding: { xs: "8px 24px", sm: "16px 24px" },
                height: "100%",
                maxHeight: { xs: "165px", lg: "265px" },
                overflowY: "auto",
              }}
            >
              {searchResult && searchResult.length > 0 ? (
                searchResult.map((item) => {
                  const {
                    brand: { id: brandId },
                    fullTextSearch,
                  } = item;
                  return (
                    <Box
                      key={`search-result-${brandId}${item.model ? `-${item.model.id}` : ""}`}
                      onClick={() => onSelect(item)}
                      sx={{
                        padding: 1,
                        cursor: "pointer",
                        "&:hover": {
                          background: color.blackO1,
                        },
                      }}
                    >
                      <Typography sx={{ ...textSx }}>{highlightText(fullTextSearch)}</Typography>
                    </Box>
                  );
                })
              ) : (
                <SearchHistory />
              )}
            </Stack>
          </Stack>
        </Fade>
      </>
    );
  },
);

SmartSearchResult.displayName = "SmartSearchResult";

export default memo(SmartSearchResult);
